// src/pages/Organizations.jsx
import { useState, useEffect, useCallback } from "react";
import { Trash2, Users, Copy, RefreshCw, CheckCircle2, AlertTriangle, X, ShieldAlert } from "lucide-react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { redirectToLogin } from "../utils/auth";

const frosted =
  "border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-sm shadow-[0_0_10px_rgba(56,189,248,0.05)]";

/* ───────────────── Dismissible Alert ───────────────── */
function AlertBanner({ kind = "success", children, onClose }) {
  const isSuccess = kind === "success";
  const border = isSuccess ? "border-emerald-500/30" : "border-red-500/30";
  const bg = isSuccess ? "bg-emerald-500/10" : "bg-red-500/10";
  const text = isSuccess ? "text-emerald-200" : "text-red-200";
  const Icon = isSuccess ? CheckCircle2 : AlertTriangle;
  return (
    <div role="alert" className={`relative rounded-lg ${border} ${bg} px-3 py-2 text-sm ${text}`}>
      <div className="flex items-start gap-2 pr-6">
        <Icon className="h-4 w-4 mt-[2px]" />
        <div className="leading-relaxed">{children}</div>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="absolute right-1.5 top-1.5 rounded-md p-1 text-inherit hover:bg-white/10"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/* ───────────────── Confirm Regenerate Modal ───────────────── */
function ConfirmRegenerateModal({ open, onClose, onConfirm, isLoading }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="regen-title"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative z-[101] w-full max-w-md rounded-2xl border border-sky-900/40 bg-[#0a1224]/90 p-5 shadow-[0_0_20px_rgba(56,189,248,0.08)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 shrink-0 rounded-xl bg-sky-500/15 border border-sky-600/30 flex items-center justify-center">
            <svg width="18" height="18" viewBox="0 0 24 24" className="text-sky-300">
              <path fill="currentColor" d="M13 2v6h6v2h-6v6h-2v-6H5V8h6V2z"/>
            </svg>
          </div>
          <h3 id="regen-title" className="text-base font-semibold text-white">
            Regenerate API Key?
          </h3>
        </div>

        <p className="mt-3 text-sm text-neutral-300">
          This will <span className="text-white font-medium">immediately replace</span> the current key.
          All clients using the old key will stop working until updated.
        </p>

        <div className="mt-4 rounded-lg bg-neutral-900/50 border border-neutral-800 px-3 py-2 text-xs text-neutral-400">
          Tip: Export your current environment variables before rotating.
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-neutral-700 bg-transparent px-3 py-2 text-sm text-neutral-300 hover:bg-white/5"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-lg border border-sky-600/40 bg-sky-500/20 px-3 py-2 text-sm text-sky-200 hover:bg-sky-500/30 disabled:opacity-60"
          >
            {isLoading && (
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" className="opacity-20" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path d="M4 12a8 8 0 018-8" className="opacity-80" stroke="currentColor" strokeWidth="4" fill="none"/>
              </svg>
            )}
            Regenerate
          </button>
        </div>

        <button
          onClick={onClose}
          className="absolute right-3 top-3 rounded-md p-1 text-neutral-400 hover:text-white hover:bg-white/10"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

/* ───────────────── Confirm Remove Modal ───────────────── */
function ConfirmRemoveModal({ open, email, onClose, onConfirm, isLoading }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="remove-title"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative z-[101] w-full max-w-md rounded-2xl border border-red-500/30 bg-[#0a1224]/90 p-5 shadow-[0_0_20px_rgba(239,68,68,0.15)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 shrink-0 rounded-xl bg-red-500/15 border border-red-500/30 flex items-center justify-center">
            <svg width="18" height="18" viewBox="0 0 24 24" className="text-red-300">
              <path fill="currentColor" d="M9 3v1H4v2h1v12a3 3 0 003 3h8a3 3 0 003-3V6h1V4h-5V3H9zm9 3v12a1 1 0 01-1 1H9a1 1 0 01-1-1V6h10zM9 8h2v8H9V8zm4 0h2v8h-2V8z"/>
            </svg>
          </div>
          <h3 id="remove-title" className="text-base font-semibold text-white">
            Remove user?
          </h3>
        </div>

        <p className="mt-3 text-sm text-neutral-300">
          This action will remove <span className="text-white font-medium">{email}</span> from your organization.
        </p>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-neutral-700 bg-transparent px-3 py-2 text-sm text-neutral-300 hover:bg-white/5"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-500/20 px-3 py-2 text-sm text-red-100 hover:bg-red-500/30 disabled:opacity-60"
          >
            {isLoading && (
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" className="opacity-20" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path d="M4 12a8 8 0 018-8" className="opacity-80" stroke="currentColor" strokeWidth="4" fill="none"/>
              </svg>
            )}
            Remove
          </button>
        </div>

        <button
          onClick={onClose}
          className="absolute right-3 top-3 rounded-md p-1 text-neutral-400 hover:text-white hover:bg-white/10"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

/* ───────────────── Reusable CopyBox ───────────────── */
function CopyBox({ label, value }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Copy failed", e);
    }
  };
  return (
    <div>
      <span className="font-medium text-sky-400">{label}</span>
      <div className="mt-1 flex items-center justify-between rounded bg-[#000414] p-3 text-sm border border-sky-700/40">
        <code className="break-all">{value}</code>
        <button
          onClick={handleCopy}
          className="ml-3 text-sky-400 hover:text-sky-200"
          title="Copy to clipboard"
        >
          <Copy className="h-4 w-4" />
        </button>
      </div>
      {copied && <div className="text-xs text-green-400 mt-1">Copied!</div>}
    </div>
  );
}

export default function Organizations() {
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Gating flags from server (Option C)
  const [planLimited, setPlanLimited] = useState(false);
  const [gateMessage, setGateMessage] = useState(null);

  const [organization, setOrganization] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [savingSso, setSavingSso] = useState(false);

  // API tab states
  const [apiInfo, setApiInfo] = useState(null); // { api_key, remaining_calls }
  const [showKey, setShowKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [apiSuccess, setApiSuccess] = useState(null);

  // Users (LEFT pane) – now scoped
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [usersSuccess, setUsersSuccess] = useState(null);
  const [removingEmail, setRemovingEmail] = useState(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);

  // Invite (RIGHT pane) – now scoped
  const [inviteError, setInviteError] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(null);

  // License tab states
  const [licenseLoading, setLicenseLoading] = useState(true);
  const [licenseError, setLicenseError] = useState(null);

  // Tabs
  const [activeTab, setActiveTab] = useState("license"); // license | adduser | api | sso

  // SSO form
  const [tenantId, setTenantId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState(""); // never prefilled
  const redirectUrl = "https://platform.whiteintel.io/login";
  const [ssoVerified, setSsoVerified] = useState(false);
  const [forceSso, setForceSso] = useState(false);
  const [ssoError, setSsoError] = useState(null);
  const [ssoSuccess, setSsoSuccess] = useState(null);

  // JIRA FORM

  const [jiraIssueType, setJiraIssueType] = useState("Bug"); // "Issue" | "Bug"
  const [jiraUrl, setJiraUrl] = useState("");
  const [jiraUsername, setJiraUsername] = useState("");
  const [jiraToken, setJiraToken] = useState("");
  const [jiraProjectKey, setJiraProjectKey] = useState("");
  const [jiraActive, setJiraActive] = useState(false);
  const [jiraDisableLoading, setJiraDisableLoading] = useState(false);
// NEW: test connection UX state
  const [jiraTestLoading, setJiraTestLoading] = useState(false);
  const [jiraBanner, setJiraBanner] = useState(null); // { kind: 'success'|'error', message: string }
  const [jiraSaveLoading, setJiraSaveLoading] = useState(false);

//SLACK
const [slackWebhookUrl, setSlackWebhookUrl] = useState('');
const [slackChannelName, setSlackChannelName] = useState('');
const [slackBotName, setSlackBotName] = useState('');

const [slackBanner, setSlackBanner] = useState(null);
const [slackActive, setSlackActive] = useState(false);
const [slackSaveLoading, setSlackSaveLoading] = useState(false);
const [slackTestLoading, setSlackTestLoading] = useState(false);
const [slackDisableLoading, setSlackDisableLoading] = useState(false);


const handleDisableSlack = async () => {
  setSlackBanner(null);
  setSlackDisableLoading(true);

  try {
    const res = await fetch("https://whiteintel.io/api/regular/app/v2/slack/disable_slack.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwtToken()}`,
      },
    });

    const json = await res.json().catch(() => ({}));

    if (res.ok && json?.success) {
      setSlackActive(false);
      setSlackBanner({ kind: "success", message: "Slack integration disabled." });
    } else {
      setSlackBanner({
        kind: "error",
        message: json?.error || `Failed to disable Slack integration (HTTP ${res.status})`,
      });
    }
  } catch (e) {
    setSlackBanner({
      kind: "error",
      message: e?.message || "Network error while disabling Slack integration.",
    });
  } finally {
    setSlackDisableLoading(false);
  }
};



const testSlackConnection = async () => {
  setSlackBanner(null);

  const url = slackWebhookUrl?.trim();
  if (!url) {
    setSlackBanner({
      kind: "error",
      message: "Please enter a Slack webhook URL to test.",
    });
    return;
  }

  setSlackTestLoading(true);

  try {
    const res = await fetch("https://whiteintel.io/api/regular/app/v2/slack/test_slack.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwtToken()}`,
      },
      body: JSON.stringify({ slack_webhook_url: url }),
    });

    const json = await res.json().catch(() => ({}));

    if (res.ok && json?.success) {
      setSlackBanner({ kind: "success", message: "Slack webhook tested successfully." });
    } else {
      setSlackBanner({
        kind: "error",
        message: json?.error || `Slack test failed (HTTP ${res.status})`,
      });
    }
  } catch (e) {
    setSlackBanner({
      kind: "error",
      message: e?.message || "Network error while testing Slack webhook.",
    });
  } finally {
    setSlackTestLoading(false);
  }
};

  const handleSaveSlack = async () => {
  setSlackBanner(null); // clear existing banner

  const url = slackWebhookUrl?.trim();
  const channel = slackChannelName?.trim();
  const bot = slackBotName?.trim();

  if (!url) {
    setSlackBanner({
      kind: "error",
      message: "Please provide a valid Slack Webhook URL.",
    });
    return;
  }

  setSlackSaveLoading(true);

  try {
    const res = await fetch(
      "https://whiteintel.io/api/regular/app/v2/slack/configure_slack.php",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({
          slack_webhook_url: url,
          channel_name: channel || undefined,
          bot_name: bot || undefined,
        }),
      }
    );

    const json = await res.json().catch(() => ({}));

    if (res.ok && json?.success) {
      setSlackBanner({ kind: "success", message: "Slack integration saved." });
      setSlackActive(true);
    } else {
      setSlackBanner({
        kind: "error",
        message: json?.error || `Failed to save Slack integration (HTTP ${res.status}).`,
      });
    }
  } catch (e) {
    setSlackBanner({
      kind: "error",
      message: e?.message || "Network error while saving Slack integration.",
    });
  } finally {
    setSlackSaveLoading(false);
  }
};


async function handleDisableJira() {
  if (planLimited || jiraDisableLoading) return;
  setJiraBanner(null);
  setJiraDisableLoading(true);

  try {
    const res = await fetch(
      "https://whiteintel.io/api/regular/app/v2/jira/disable_jira.php",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({}), // body unused by your endpoint, but keep JSON to satisfy guard
      }
    );

    // auth redirect handling consistent with the rest of the page
    if ([401, 419, 440].includes(res.status)) {
      redirectToLogin();
      return;
    }

    const json = await res.json().catch(() => ({}));

    if (!res.ok || json?.success !== true) {
      throw new Error(json?.error || `Failed to disable Jira (HTTP ${res.status})`);
    }

    setJiraActive(false);
    setJiraBanner({ kind: "success", message: "Jira integration disabled." });
    setJiraUsername("");
    setJiraUrl("");
    setJiraToken("");
    setJiraProjectKey("");
    setJiraIssueType("");
  } catch (e) {
    setJiraBanner({ kind: "error", message: e?.message || "Failed to disable Jira." });
  } finally {
    setJiraDisableLoading(false);
  }
}



  const handleSaveJira = async () => {
  setJiraBanner(null);

  const url = jiraUrl?.trim();
  const user = jiraUsername?.trim();
  const token = jiraToken?.trim();
  const project = jiraProjectKey?.trim();
  const type = jiraIssueType?.trim();

  if (!url || !user || !token || !project || !type) {
    setJiraBanner({
      kind: "error",
      message: "Please fill Jira URL, Username, API Key, Project Key and Ticket Type.",
    });
    return;
  }

  setJiraSaveLoading(true);
  try {
    const res = await fetch(
      "https://whiteintel.io/api/regular/app/v2/jira/configure_jira.php",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`, // uses your existing helper
        },
        body: JSON.stringify({
          jira_url: url,
          jira_username: user,
          jira_token: token,
          jira_project: project,
          jira_ticket_type: type,
        }),
      }
    );

    const json = await res.json().catch(() => ({}));

    if (res.ok && json?.success) {
      setJiraActive(true);
      setJiraBanner({ kind: "success", message: "Jira integration saved." });
    } else {
      setJiraBanner({
        kind: "error",
        message: json?.error || `Failed to save Jira integration (HTTP ${res.status}).`,
      });
    }
  } catch (e) {
    setJiraBanner({
      kind: "error",
      message: e?.message || "Network error while saving Jira integration.",
    });
  } finally {
    setJiraSaveLoading(false);
  }
};


  const testJiraConnection = async () => {
  // clear any previous banner
  setJiraBanner(null);

  // quick client-side checks
  if (!jiraUrl?.trim() || !jiraUsername?.trim() || !jiraToken?.trim()) {
    setJiraBanner({ kind: "error", message: "Please fill Jira URL, Username and API Key first." });
    return;
  }

  setJiraTestLoading(true);
  try {
    const res = await fetch(
      "https://whiteintel.io/api/regular/app/v2/jira/test_jira.php",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({
          jira_url: jiraUrl.trim(),
          jira_username: jiraUsername.trim(),
          jira_token: jiraToken.trim(),
        }),
      }
    );

    const json = await res.json().catch(() => ({}));

    if (res.ok && json?.success) {
      const who = json.displayName || json.accountId || jiraUsername;
      setJiraBanner({
        kind: "success",
        message: `Jira connection successful. Authenticated as ${who}.`,
      });
    } else {
      setJiraBanner({
        kind: "error",
        message: json?.error || `Test failed (HTTP ${res.status}).`,
      });
    }
  } catch (e) {
    setJiraBanner({
      kind: "error",
      message: e?.message || "Network error while testing Jira connection.",
    });
  } finally {
    setJiraTestLoading(false);
  }
};


  function getJwtToken() {
    try {
      return (
        localStorage.getItem("token") ||
        sessionStorage.getItem("jwtToken") ||
        window.WHITEINTEL_TOKEN ||
        ""
      );
    } catch {
      return "";
    }
  }

  const removeUser = async (email) => {
    if (planLimited || !email) return;

    try {
      setUsersError(null);
      setUsersSuccess(null);
      setRemovingEmail(email);

      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/organizations/delete_user.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          body: JSON.stringify({ email }),
        }
      );

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }

      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw new Error(json?.error || `Failed to remove user (HTTP ${res.status})`);
      }

      setUsers((prev) => prev.filter((u) => u.email !== email));
      setUsersSuccess("User removed successfully.");
    } catch (e) {
      setUsersError(e?.message || "Failed to remove user.");
    } finally {
      setRemovingEmail(null);
      setRemoveOpen(false);
      setPendingEmail(null);
    }
  };

  async function saveSsoConfig() {
    setSsoError(null);
    setSsoSuccess(null);

    if (!tenantId?.trim() || !clientId?.trim() || !redirectUrl?.trim()) {
      setSsoError("Please fill all SSO fields (Tenant ID, Client ID, Redirect URL).");
      return;
    }

    setSavingSso(true);
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/organizations/configure_sso.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(getJwtToken() ? { Authorization: `Bearer ${getJwtToken()}` } : {}),
          },
          body: JSON.stringify({
            provider: "azure",
            tenant_id: tenantId.trim(),
            client_id: clientId.trim(),
            client_secret: (clientSecret ?? "").trim(), // optional for public clients
            redirect_uri: redirectUrl.trim(),
            enforce_sso: !!forceSso,
          }),
        }
      );

      if ([401, 419, 440].includes(res.status)) {
        setSsoError("Your session has expired. Please sign in again.");
        return;
      }

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setSsoError(json?.error || `Failed to save SSO settings (HTTP ${res.status}).`);
        return;
      }

      if (json?.success) {
        setSsoVerified(!!json.verified || true);
        setSsoSuccess("SSO configuration saved successfully.");
      } else {
        setSsoError(json?.error || "Could not verify SSO settings. Please check values and try again.");
      }
    } catch (err) {
      setSsoError(err?.message || "Network error while saving SSO settings.");
    } finally {
      setSavingSso(false);
    }
  }

  /* ── Populate tenantId/clientId when organization arrives ── */
  useEffect(() => {
    if (!organization) return;

    const t = organization.azure_tenant_id ?? "";
    const c = organization.azure_client_id ?? organization.client_id ?? "";

    setTenantId(t);
    setClientId(c);
    setClientSecret("");

    setForceSso(!!(organization.sso_enabled ?? organization.enforce_sso));
    setSsoVerified(Boolean(t && c));
  }, [organization]);

  /* ── Fetch license (Option C) ── */
  useEffect(() => {
    const fetchLicense = async () => {
      try {
        setLicenseLoading(true);
        setLicenseError(null);
        setPlanLimited(false);
        setGateMessage(null);

        const res = await fetch(
          "https://whiteintel.io/api/regular/app/v2/organizations/get_license.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getJwtToken()}`,
            },
          }
        );

        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }

        const data = await res.json();

        if (data?.plan_limited) {
          setPlanLimited(true);
          setGateMessage(data?.message || "Upgrade required for unlocking full features.");
          setOrganization(data?.organization || null);
          setApiInfo(data?.api || null);
          return;
        }

        const org = data?.organization;
        if (!org) throw new Error("Invalid license response");

        setOrganization(org);
        setApiInfo(data?.api || null);

        if(data?.jira?.active){
        setJiraUrl(data?.jira?.jira_url);
        setJiraUsername(data?.jira?.jira_username);
        setJiraToken(data?.jira?.jira_token);
        setJiraProjectKey(data?.jira?.jira_project);
        setJiraIssueType(data?.jira?.jira_ticket_type);
        setJiraActive(data?.jira?.active);
        }

        if(data?.slack?.active)
        {

          setSlackWebhookUrl(data?.slack?.slack_webhook_url);
          setSlackChannelName(data?.slack?.channel_name);
          setSlackBotName(data?.slack?.bot_name);
          setSlackActive(data?.slack?.active);
        }

      } catch (err) {
        console.error(err);
        setLicenseError("Failed to load license info.");
        setPlanLimited(false);
      } finally {
        setLicenseLoading(false);
      }
    };

    fetchLicense();
  }, []);

  /* ── Fetch users if not gated ── */
  useEffect(() => {
    if (planLimited) {
      setUsersLoading(false);
      return;
    }
    const fetchUsers = async () => {
      try {
        setUsersLoading(true);
        const res = await fetch(
          "https://whiteintel.io/api/regular/app/v2/organizations/get_users.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getJwtToken()}`,
            },
          }
        );

        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }

        const data = await res.json();
        setUsers(data.users || []);
      } catch (err) {
        console.error(err);
        setUsersError("Failed to load users.");
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, [planLimited]);

  /* ── Add User ── */
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("analyst");
  const addUser = async () => {
    if (!newEmail.trim() || planLimited) return;
    try {
      setInviteError(null);
      setInviteSuccess(null);

      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/organizations/invite_user.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          body: JSON.stringify({
            email: newEmail.trim(),
            role: newRole,
          }),
        }
      );

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }

      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Add user failed");

      setUsers((prev) => [
        {
          email: newEmail.trim(),
          organization_role: newRole,
          joined_at: new Date().toISOString(),
        },
        ...prev,
      ]);
      setNewEmail("");
      setNewRole("analyst");
      setInviteSuccess("User invited successfully.");
    } catch (e) {
      setInviteError(e.message || "Failed to add user");
    }
  };

  /* ── API: Copy + Regenerate ── */
  const handleCopyKey = useCallback(async () => {
    if (!apiInfo?.api_key || planLimited) return;
    try {
      await navigator.clipboard.writeText(apiInfo.api_key);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 1500);
    } catch (e) {
      console.error("Copy failed", e);
    }
  }, [apiInfo?.api_key, planLimited]);

  const handleRegenerateKey = useCallback(async () => {
    if (planLimited) return;
    setRegenLoading(true);
    setApiError(null);
    setApiSuccess(null);
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/apikeys/regenerate_api.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          body: JSON.stringify({}),
        }
      );

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }

      const data = await res.json();
      if (!res.ok || !data?.apikey) {
        throw new Error(data?.error || data?.message || "Failed to regenerate key");
      }

      setApiInfo((prev) => ({ ...(prev || {}), api_key: data.apikey }));
      setShowKey(false);
      setApiSuccess("API key regenerated successfully.");
    } catch (err) {
      console.error(err);
      setApiError(err.message || "Unknown error");
    } finally {
      setRegenLoading(false);
    }
  }, [planLimited]);

  const maskedKey = apiInfo?.api_key?.replace(/.(?=.{4})/g, "•") || "—";

  const handleUpgrade = () => {
    try {
      window.location.href = "/upgrades";
    } catch {
      window.open("mailto:sales@whiteintel.io", "_blank");
    }
  };

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      {/* Header spacer */}
      <header
        className="bg-transparent px-6 py-4 transition-all duration-300"
        style={{ marginLeft: collapsed ? "72px" : "240px" }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h1 className="text-[24px] font-bold leading-tight">Organization Settings</h1>
          </div>
        </div>
      </header>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="watchlists"
          onChange={() => {}}
        />

        {/* Content */}
        <main className="relative flex-1 px-4 md:px-6">
          {/* ✔ Plan-limited card just under the header */}
          {planLimited && (
            <div className="mt-3 max-w-2xl">
              <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-5 py-4 text-amber-100 shadow-lg backdrop-blur-md">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="h-5 w-5 mt-0.5" />
                  <div>
                    <div className="font-semibold text-amber-100">Organization features are locked</div>
                    <div className="text-sm opacity-90 mt-1">
                      Organization features are not available under your current subscription.
                      Please upgrade to Enterprise or Threat Intel license to unlock organization features such as multiple seats, SSO, Jira and Slack.
                    </div>
                    <div className="mt-3">
                      <button
                        onClick={handleUpgrade}
                        className="rounded-md border border-amber-500/40 bg-transparent px-3 py-1.5 text-sm hover:bg-amber-500/10"
                      >
                        Upgrade plan
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!planLimited && (
            <div className={`mt-6 grid grid-cols-1 lg:grid-cols-[1.25fr_0.75fr] gap-6 transition`}>
              {/* LEFT: Users list */}
              <div className={`rounded-2xl ${frosted} h-[70vh] flex flex-col`}>
                <div className="px-4 pt-4">
                  <div className="flex items-center justify-between border-b border-sky-900/30 pb-3">
                    <div className="text-lg font-semibold flex items-center gap-2">
                      <Users className="h-5 w-5 text-sky-300" /> Users
                    </div>
                    <div className="text-sm text-neutral-400">
                      {usersLoading ? "Loading…" : `${users.length} users`}
                    </div>
                  </div>
                </div>

                {/* Optional banners for LEFT pane */}
                {usersError && (
                  <div className="px-4 pt-3">
                    <AlertBanner kind="error" onClose={() => setUsersError(null)}>
                      {usersError}
                    </AlertBanner>
                  </div>
                )}
                {usersSuccess && (
                  <div className="px-4 pt-3">
                    <AlertBanner kind="success" onClose={() => setUsersSuccess(null)}>
                      {usersSuccess}
                    </AlertBanner>
                  </div>
                )}

                <div className="flex-1 overflow-auto scrollbar-thin scrollbar-thumb-neutral-700/70 scrollbar-track-transparent">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-[#0a1224]/90 backdrop-blur-sm">
                      <tr className="text-neutral-400 border-b border-sky-900/30">
                        <th className="px-2 py-3 text-left font-semibold">Email</th>
                        <th className="px-2 py-3 text-left font-semibold">Role</th>
                        <th className="px-2 py-3 text-left font-semibold">Joined</th>
                        <th className="px-2 py-3 text-left font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-800/80">
                      {usersError ? (
                        <tr>
                          <td colSpan={4} className="px-4 py-10 text-center text-red-400">
                            {usersError}
                          </td>
                        </tr>
                      ) : users.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-4 py-10 text-center text-neutral-400">
                            {planLimited ? "Upgrade required to view users." : "No users found."}
                          </td>
                        </tr>
                      ) : (
                        users.map((u, idx) => (
                          <tr key={`${u.email}-${idx}`} className="hover:bg-white/5">
                            <td className="px-2 py-3 break-all">{u.email}</td>
                            <td className="px-2 py-3 capitalize">{u.organization_role}</td>
                            <td className="px-2 py-3">{u.joined_at ? u.joined_at.split(" ")[0] : "—"}</td>
                            <td className="px-2 py-3">
                              <button
                                onClick={() => { setPendingEmail(u.email); setRemoveOpen(true); }}
                                className="text-red-400 hover:text-red-300 inline-flex items-center gap-1 disabled:opacity-60"
                                title="Remove user"
                                disabled={planLimited}
                              >
                                <Trash2 className="h-4 w-4" />
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* RIGHT: Tabs */}
              <div className={`rounded-2xl ${frosted} h-[70vh] flex flex-col p-5`}>
                {/* Tabs */}
                <div className="flex border-b border-neutral-800 mb-4">
                  <button
                    onClick={() => setActiveTab("license")}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === "license"
                        ? "text-white border-b-2 border-white"
                        : "text-neutral-400 hover:text-white"
                    }`}
                  >
                    License
                  </button>
                  <button
                    onClick={() => setActiveTab("adduser")}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === "adduser"
                        ? "text-white border-b-2 border-white"
                        : "text-neutral-400 hover:text-white"
                    }`}
                    disabled={planLimited}
                  >
                   Users
                  </button>
                  <button
                    onClick={() => setActiveTab("api")}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === "api"
                        ? "text-white border-b-2 border-white"
                        : "text-neutral-400 hover:text-white"
                    }`}
                    disabled={planLimited}
                  >
                    API
                  </button>
                  <button
                    onClick={() => setActiveTab("sso")}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === "sso"
                        ? "text-white border-b-2 border-white"
                        : "text-neutral-400 hover:text-white"
                    }`}
                    disabled={planLimited}
                  >
                    SSO
                  </button>

                                    <button
                    onClick={() => setActiveTab("Jira")}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === "Jira"
                        ? "text-white border-b-2 border-white"
                        : "text-neutral-400 hover:text-white"
                    }`}
                    disabled={planLimited}
                  >
                    Jira
                  </button>


                                            <button
                    onClick={() => setActiveTab("Slack")}
                    className={`px-4 py-2 text-sm font-medium ${
                      activeTab === "Slack"
                        ? "text-white border-b-2 border-white"
                        : "text-neutral-400 hover:text-white"
                    }`}
                    disabled={planLimited}
                  >
                    Slack
                  </button>
                </div>

                {/* Tab content */}
                <div className="flex-1 overflow-auto">
                  {activeTab === "license" && (
                    <div className="space-y-3 text-sm text-neutral-300">
                      <h3 className="text-lg font-semibold text-white mb-3">License Details</h3>
                      {licenseLoading ? (
                        <div className="text-neutral-400">Loading license…</div>
                      ) : licenseError ? (
                        <AlertBanner kind="error" onClose={() => setLicenseError(null)}>
                          {licenseError}
                        </AlertBanner>
                      ) : organization ? (
                        <>
                          {[
                            ["Organization", organization.name],
                            ["Plan", organization.plan],
                            ["Seats", organization.seats],
                            ["CSV Export Limit", organization.csv_export_limit],
                            ["Domain Identifiers", organization.domain_identifiers],
                            ["Email Identifiers", organization.email_identifiers],
                            ["IP Identifiers", organization.ip_identifiers],
                            ["Computername Identifiers", organization.computername_identifiers],
                            ["API Limit", organization.api_limit],
                            ["Expires At", organization.expires_at],
                          ].map(([label, value]) => (
                            <div key={label} className="flex justify-between border-b border-neutral-800 pb-1">
                              <span className="text-neutral-400">{label}:</span>
                              <span className="font-medium text-white">{value}</span>
                            </div>
                          ))}
                        </>
                      ) : (
                        <div className="text-neutral-400">
                          {planLimited ? "Upgrade required to view license details." : "No license data."}
                        </div>
                      )}

                      <p className="mt-3 text-sm text-neutral-500">
  Version <span className="text-neutral-400 font-medium">4.0.3</span> (stable)
</p>
                    </div>
                    
                  )}

                  {activeTab === "adduser" && (
                    <div className="space-y-4">
                      {inviteError && (
                        <AlertBanner kind="error" onClose={() => setInviteError(null)}>
                          {inviteError}
                        </AlertBanner>
                      )}
                      {inviteSuccess && (
                        <AlertBanner kind="success" onClose={() => setInviteSuccess(null)}>
                          {inviteSuccess}
                        </AlertBanner>
                      )}

                      <div>
                        <h3 className="text-lg font-semibold text-white mb-3">Add User</h3>
                        <label className="mb-1 block text-sm text-neutral-300">User Email</label>
                        <input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          placeholder="user@company.com"
                          className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2 text-sm outline-none"
                          required
                          disabled={planLimited}
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-sm text-neutral-300">Role</label>
                        <select
                          value={newRole}
                          onChange={(e) => setNewRole(e.target.value)}
                          className="w-full rounded-lg border border-sky-900/30 bg-neutral-900 text-white px-3 py-2 text-sm outline-none"
                          disabled={planLimited}
                        >
                          <option value="admin">Admin</option>
                          <option value="analyst">Analyst</option>
                        </select>
                      </div>

                      <button
                        onClick={addUser}
                        disabled={!newEmail.trim() || planLimited}
                        className="rounded-lg border border-sky-600/40 bg-sky-500/10 px-4 py-2 text-sm text-sky-200 hover:bg-sky-500/20 disabled:opacity-60"
                      >
                        Invite User
                      </button>
                    </div>
                  )}

                  {activeTab === "api" && (
                    <div className="flex-1 overflow-auto p-6">
                      {apiError && (
                        <AlertBanner kind="error" onClose={() => setApiError(null)}>
                          {apiError}
                        </AlertBanner>
                      )}
                      {apiSuccess && (
                        <AlertBanner kind="success" onClose={() => setApiSuccess(null)}>
                          {apiSuccess}
                        </AlertBanner>
                      )}
                  <h3 className="text-lg font-semibold text-white mb-3">API Key</h3>
                      <div className="max-w-xl rounded-2xl border border-neutral-700/50 bg-[#0a1224]/70 p-5 shadow-sm">
                        {/* API Key row */}
                        <div className="flex items-center justify-between bg-neutral-900/50 rounded-lg px-4 py-3">
                          <span className="font-mono text-sm tracking-wider text-neutral-300 select-none break-all">
                            {showKey ? apiInfo?.api_key || "—" : (apiInfo?.api_key ? maskedKey : "—")}
                          </span>

                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => setShowKey((v) => !v)}
                              className="rounded-md px-2 py-1 text-xs text-neutral-400 hover:text-white hover:bg-white/10 transition"
                              disabled={!apiInfo?.api_key || planLimited}
                              title={showKey ? "Hide API key" : "Show API key"}
                            >
                              {showKey ? "Hide" : "Show"}
                            </button>
                            <button
                              onClick={handleCopyKey}
                              className="rounded-md px-2 py-1 text-xs text-neutral-400 hover:text-white hover:bg-white/10 transition"
                              disabled={!apiInfo?.api_key || planLimited}
                              title={copiedKey ? "Copied" : "Copy to clipboard"}
                            >
                              {copiedKey ? "Copied" : "Copy"}
                            </button>
                          </div>
                        </div>

                        {/* Remaining calls */}
                        <div className="mt-3 text-xs text-neutral-300">
                          <div className="flex items-center justify-between">
                            <span className="text-neutral-400">Remaining calls (today)</span>
                            <span className="font-medium">
                              {typeof apiInfo?.remaining_calls === "number" ? apiInfo.remaining_calls : "—"}
                            </span>
                          </div>
                        </div>

                        {/* Regenerate */}
                        <div className="mt-4 flex items-center justify-between">
                          <p className="text-xs text-neutral-500">
                            Rotating will immediately invalidate the old key. Ensure all integrations are updated.
                          </p>
                          <button
                            onClick={() => setConfirmOpen(true)}
                            disabled={regenLoading || planLimited}
                            className="inline-flex items-center gap-2 rounded-lg border border-sky-600/40 bg-sky-500/20 hover:bg-sky-500/30 px-3 py-2 text-xs text-sky-300 transition disabled:opacity-60"
                            title="Regenerate API Key"
                          >
                            <RefreshCw className="h-4 w-4" />
                            {regenLoading ? "Regenerating…" : "Regenerate API Key"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "Jira" && (
                    <div className="space-y-4 text-sm text-neutral-300">
                      {jiraBanner && (
                        <div className="mb-3">
                          <AlertBanner kind={jiraBanner.kind} onClose={() => setJiraBanner(null)}>
                            {jiraBanner.message}
                          </AlertBanner>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-white">Jira Configuration</h3>

                    {jiraActive && (
                      <button
                        onClick={handleDisableJira}
                        disabled={planLimited || jiraDisableLoading}
                        className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-sm text-red-200 hover:bg-red-500/20 disabled:opacity-60"
                        title="Disable Jira integration for this organization"
                      >
                        {jiraDisableLoading ? "Disabling…" : "Disable"}
                      </button>
                    )}
                  </div>


                    
                      <div>
                        <label className="mb-1 block text-sm text-neutral-300">Jira URL</label>
                        <input
                          type="text"
                          value={jiraUrl}
                          onChange={(e) => setJiraUrl(e.target.value)}
                          placeholder="company.atlassian.net"
                          className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2 text-sm outline-none"
                          disabled={planLimited}
                        />
                      </div>

                  
                      <div>
                        <label className="mb-1 block text-sm text-neutral-300">Jira Email</label>
                        <input
                          type="text"
                          value={jiraUsername}
                          onChange={(e) => setJiraUsername(e.target.value)}
                          placeholder="Email address of Jira account"
                          className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2 text-sm outline-none"
                          disabled={planLimited}
                        />
                      </div>

                      {/* Client Secret (optional for SPA/public, required for confidential) */}
                      <div>
                        <label className="mb-1 block text-sm text-neutral-300">API Key</label>
                        <input
                          type="password"
                          value={jiraToken}
                          onChange={(e) => setJiraToken(e.target.value)}
                          placeholder="Jira account API key"
                          className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2 text-sm outline-none"
                          disabled={planLimited}
                        />
                      </div>


                        <div>
                        <label className="mb-1 block text-sm text-neutral-300">Jira Project Code</label>
                        <input
                          type="text"
                          value={jiraProjectKey}
                          onChange={(e) => setJiraProjectKey(e.target.value)}
                          placeholder="Jira project code, e.g OPS"
                          className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2 text-sm outline-none"
                          disabled={planLimited}
                        />
                      </div>

                    <div>
                      <label className="mb-1 block text-sm text-neutral-300">Ticket Type</label>
                      <select
                        value={jiraIssueType}
                        onChange={(e) => setJiraIssueType(e.target.value)}
                        className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2 text-sm outline-none"
                        disabled={planLimited}
                      >
                        <option value="Bug">Bug</option>
                        <option value="Task">Task</option>
                      </select>
                    </div>


                      <p className="text-xs text-red-400 mt-1">
                        Ensure user has only required privileges to create tickets.
                      </p>

                                           {/* Save */}
                <div className="mt-3 flex items-center gap-2">
                  <button
                    onClick={testJiraConnection}
                    className="rounded-lg border border-sky-600/40 bg-sky-500/10 px-4 py-2 text-sm text-sky-200 hover:bg-sky-500/20 disabled:opacity-60"
                    disabled={
                      planLimited ||
                      jiraTestLoading ||
                      !jiraUrl?.trim() ||
                      !jiraUsername?.trim() ||
                      !jiraToken?.trim()
                    }
                  >
                    {jiraTestLoading ? "Testing…" : "Test Connection"}
                  </button>

                    <button
                      onClick={handleSaveJira}
                      className="rounded-lg border border-sky-600/40 bg-sky-500/10 px-4 py-2 text-sm text-sky-200 hover:bg-sky-500/20 disabled:opacity-60"
                      disabled={
                        planLimited ||
                        jiraSaveLoading ||
                        !jiraUrl.trim() ||
                        !jiraUsername.trim() ||
                        !jiraToken.trim() ||
                        !jiraProjectKey.trim() ||
                        !jiraIssueType.trim()
                      }
                    >
                      {jiraSaveLoading ? "Saving…" : "Save Configuration"}
                    </button>
                </div>

                    </div>
                  )}


                                   {activeTab === "Slack" && (
  <div className="space-y-4 text-sm text-neutral-300">
    {slackBanner && (
      <div className="mb-3">
        <AlertBanner kind={slackBanner.kind} onClose={() => setSlackBanner(null)}>
          {slackBanner.message}
        </AlertBanner>
      </div>
    )}

    <div className="flex items-center justify-between">
      <h3 className="text-lg font-semibold text-white">Slack Configuration</h3>

      {slackActive && (
        <button
          onClick={handleDisableSlack}
          disabled={planLimited || slackDisableLoading}
          className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-sm text-red-200 hover:bg-red-500/20 disabled:opacity-60"
          title="Disable Slack integration for this organization"
        >
          {slackDisableLoading ? "Disabling…" : "Disable"}
        </button>
      )}
    </div>

    <div>
      <label className="mb-1 block text-sm text-neutral-300">Webhook URL</label>
      <input
        type="text"
        value={slackWebhookUrl}
        onChange={(e) => setSlackWebhookUrl(e.target.value)}
        placeholder="https://hooks.slack.com/services/..."
        className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2 text-sm outline-none"
        disabled={planLimited}
      />
    </div>

    <div>
      <label className="mb-1 block text-sm text-neutral-300">Slack Channel</label>
      <input
        type="text"
        value={slackChannelName}
        onChange={(e) => setSlackChannelName(e.target.value)}
        placeholder="#alerts (optional)"
        className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2 text-sm outline-none"
        disabled={planLimited}
      />
    </div>

    <div>
      <label className="mb-1 block text-sm text-neutral-300">Bot Name</label>
      <input
        type="text"
        value={slackBotName}
        onChange={(e) => setSlackBotName(e.target.value)}
        placeholder="e.g. WhiteIntelBot (optional)"
        className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2 text-sm outline-none"
        disabled={planLimited}
      />
    </div>

    <div className="mt-3 flex items-center gap-2">
      <button
        onClick={testSlackConnection}
        className="rounded-lg border border-sky-600/40 bg-sky-500/10 px-4 py-2 text-sm text-sky-200 hover:bg-sky-500/20 disabled:opacity-60"
        disabled={
          planLimited ||
          slackTestLoading ||
          !slackWebhookUrl?.trim()
        }
      >
        {slackTestLoading ? "Testing…" : "Test Connection"}
      </button>

      <button
        onClick={handleSaveSlack}
        className="rounded-lg border border-sky-600/40 bg-sky-500/10 px-4 py-2 text-sm text-sky-200 hover:bg-sky-500/20 disabled:opacity-60"
        disabled={
          planLimited ||
          slackSaveLoading ||
          !slackWebhookUrl?.trim()
        }
      >
        {slackSaveLoading ? "Saving…" : "Save Configuration"}
      </button>
    </div>
  </div>
)}


                      {activeTab === "sso" && (
                    <div className="space-y-4 text-sm text-neutral-300">
                      {ssoError && (
                        <AlertBanner kind="error" onClose={() => setSsoError(null)}>
                          {ssoError}
                        </AlertBanner>
                      )}
                      {ssoSuccess && (
                        <AlertBanner kind="success" onClose={() => setSsoSuccess(null)}>
                          {ssoSuccess}
                        </AlertBanner>
                      )}

                      <h3 className="text-lg font-semibold text-white">Microsoft SSO Configuration</h3>

                      {/* Tenant ID */}
                      <div>
                        <label className="mb-1 block text-sm text-neutral-300">Tenant ID</label>
                        <input
                          type="text"
                          value={tenantId}
                          onChange={(e) => setTenantId(e.target.value)}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2 text-sm outline-none"
                          disabled={planLimited}
                        />
                      </div>

                      {/* Client ID */}
                      <div>
                        <label className="mb-1 block text-sm text-neutral-300">Client ID</label>
                        <input
                          type="text"
                          value={clientId}
                          onChange={(e) => setClientId(e.target.value)}
                          placeholder="Azure App Client ID"
                          className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2 text-sm outline-none"
                          disabled={planLimited}
                        />
                      </div>

                      {/* Client Secret (optional for SPA/public, required for confidential) */}
                      <div>
                        <label className="mb-1 block text-sm text-neutral-300">Client Secret</label>
                        <input
                          type="text"
                          value={clientSecret}
                          onChange={(e) => setClientSecret(e.target.value)}
                          placeholder="Azure Client Secret"
                          className="w-full rounded-lg border border-sky-900/30 bg-neutral-900/50 px-3 py-2 text-sm outline-none"
                          disabled={planLimited}
                        />
                      </div>

                      <CopyBox label="Redirect / Callback URL" value={redirectUrl} />
                      <CopyBox label="Entity ID (Identifier)" value="https://whiteintel.io" />

                      {/* Enforce SSO toggle */}
                      <div className="flex items-center mt-3">
                        <input
                          type="checkbox"
                          disabled={planLimited}
                          checked={forceSso}
                          onChange={(e) => setForceSso(e.target.checked)}
                          className="mr-2"
                        />
                        <label className="text-sm">Enforce SSO only (disables password login)</label>
                      </div>

                      <p className="text-xs text-red-400 mt-1">
                        Only enable after testing successfully, or admins may get locked out.
                      </p>


                      


                      {/* Save */}
                      <button
                        onClick={saveSsoConfig}
                        className="rounded-lg border border-sky-600/40 bg-sky-500/10 px-4 py-2 text-sm text-sky-200 hover:bg-sky-500/20 disabled:opacity-60"
                        disabled={planLimited || savingSso}
                      >
                        {savingSso ? "Saving…" : "Save Configuration"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      <ConfirmRegenerateModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        isLoading={regenLoading}
        onConfirm={async () => {
          setConfirmOpen(false);
          await handleRegenerateKey();
        }}
      />

      <ConfirmRemoveModal
        open={removeOpen}
        email={pendingEmail}
        onClose={() => { if (!removingEmail) { setRemoveOpen(false); setPendingEmail(null); } }}
        onConfirm={() => removeUser(pendingEmail)}
        isLoading={removingEmail === pendingEmail}
      />
    </div>
  );
}
